import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { FC, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  FormControllerCheckbox,
  MuiBox,
  MuiCheckbox,
  MuiDoubleLabel,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
  MuiTextField,
  MuiTypography,
  MultiOptionsDataType,
  OptionsDataType,
} from "../../../../components";
import { MuiDateTimeFieldFormController } from "../../../../components/dateTimeField/MuiDateTimeFieldFormController";
import {
  MAX_LENGTH_250,
  MAX_LENGTH_700,
  MAX_LENGTH_75,
} from "../../../../helpers/constants";
import { getValueOrBlank, isNotEmpty } from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { ViewMessageResponse } from "../../../systemMessageSearch/modelsAndSchemas/models";
import { CreateMessageKey } from "../../modelsAndSchemas/models";
import { createSystemMessageSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateSystemMessageFormWrapper } from "./CreateSystemMessageForm.style";

dayjs.extend(utc);
dayjs.extend(timezone);

interface CreateSystemMessageFormProps {
  pageId: string;
  timeZoneListOptions: OptionsDataType[];
  countryListOptions: MultiOptionsDataType[];
  isEditMessage?: boolean;
  messageData: ViewMessageResponse;
  isFormReset: boolean;
  setIsFormReset: (value: boolean) => void;
  userCountryDateFormatWithTime: string;
}

export const CreateSystemMessageForm: FC<CreateSystemMessageFormProps> = ({
  pageId,
  timeZoneListOptions,
  countryListOptions,
  isEditMessage = false,
  messageData,
  isFormReset,
  setIsFormReset,
  userCountryDateFormatWithTime,
}) => {
  const countryDayRef = useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );

  const { control, resetField, getValues, watch, setValue } = useFormContext();
  const { commonLabels, fetchLabel, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const getMessageLabel = (key: string) =>
    fetchLabel(createSystemMessageSiteCoreKeys.createMessageKey, key);

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const showLinkContainer = useMemo(() => {
    const { addLinkInformation } = getValues();
    resetField(CreateMessageKey.linkName);
    resetField(CreateMessageKey.linkUrl);
    resetField(CreateMessageKey.linkPosition);

    return addLinkInformation;
  }, [watch(CreateMessageKey.addLinkInformation)]);

  const getDateValue = (key: keyof typeof CreateMessageKey, tz?: string) => {
    const fieldValue = watch(key);
    if (!isNotEmpty(fieldValue)) return null;
    if (tz) {
      return dayjs.tz(fieldValue, userCountryDateFormatWithTime, tz);
    }
    return dayjs(fieldValue, userCountryDateFormatWithTime);
  };

  const selectedTimeZone = useMemo(() => {
    const { timeZone } = getValues();
    return timeZone;
  }, [watch(CreateMessageKey.timeZone)]);

  const isDateDisabled = useMemo(() => {
    const { timeZone } = getValues();

    if (!isFormReset) {
      // Clear the date values when timezone changes
      resetField(CreateMessageKey.fromDate);
      resetField(CreateMessageKey.toDate);
      setValue(CreateMessageKey.fromDate, "");
      setValue(CreateMessageKey.toDate, "");
    }
    setIsFormReset(false);
    return !isNotEmpty(timeZone);
  }, [watch(CreateMessageKey.timeZone)]);

  return (
    <CreateSystemMessageFormWrapper>
      {isEditMessage ? (
        <MuiGrid container className="add-form-container country-language-grid">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getMessageLabel(createSystemMessageSiteCoreKeys.messageId)}
              replaceEmptyWithDash
              value={getValueOrBlank(messageData?.messageIdFormat)}
              isValueWithEllipsis
              className="message-id"
            />
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getMessageLabel(createSystemMessageSiteCoreKeys.country)}
              replaceEmptyWithDash
              value={getValueOrBlank(messageData?.countryName)}
              isValueWithEllipsis
            />
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getMessageLabel(createSystemMessageSiteCoreKeys.language)}
              replaceEmptyWithDash
              value={getValueOrBlank(messageData?.languageName)}
              isValueWithEllipsis
            />
          </MuiGrid>
        </MuiGrid>
      ) : (
        <MuiGrid container className="add-form-container country-language-grid">
          <MuiGrid size={gridSize}>
            <FormController
              control={control}
              name={CreateMessageKey.countryAndLanguage}
              ref={countryDayRef}
            >
              <MuiMultiSelect
                label={getMessageLabel(
                  createSystemMessageSiteCoreKeys.countryAndLanguage,
                )}
                options={countryListOptions}
                showSearch
                showChip
                isMandatory
                id={`${pageId}-${CreateMessageKey.countryAndLanguage}`}
                chipId={`${pageId}-${CreateMessageKey.countryAndLanguage}-chip`}
                dialogTitle={getMessageLabel(
                  createSystemMessageSiteCoreKeys.selectedCountryAndLanguage,
                )}
                selectPlaceHolder={commonLabels.selectPlaceHolder}
                searchPlaceHolder={commonLabels.search}
                selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                doneText={commonLabels.done}
                isDialogTitleWithEllipsis
                showDoubleLabel
                showSelectAllOption
                chipDoubleLabelPattern="label [additionalInfo]"
                allARIAProps={multiSelectARIALabels}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      )}

      <AccordionHeader
        className="accordion-header"
        title={renderHeader(
          getMessageLabel(
            createSystemMessageSiteCoreKeys.messageDetailsSection,
          ),
        )}
        isExpanded={true}
        showExpandCollapseIcon={false}
      >
        <MuiGrid container className="add-form-container title">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateMessageKey.messageTitle}
            >
              <MuiTextField
                id={`${pageId}-${CreateMessageKey.messageTitle}`}
                label={getMessageLabel(createSystemMessageSiteCoreKeys.title)}
                placeholder={commonLabels.inputPlaceHolder}
                shouldTrimSpaces
                requiredARIALabel={commonARIALabel.required}
                maxLength={MAX_LENGTH_75}
                isMandatory
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
        <MuiGrid container className="add-form-container">
          <MuiGrid size={{ xs: 12, md: 9 }} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={CreateMessageKey.messageDescription}
            >
              <MuiTextField
                id={`${pageId}-${CreateMessageKey.messageDescription}`}
                label={getMessageLabel(createSystemMessageSiteCoreKeys.message)}
                placeholder={commonLabels.inputPlaceHolder}
                isTextarea
                rows={3}
                shouldTrimSpaces
                requiredARIALabel={commonARIALabel.required}
                maxLength={MAX_LENGTH_700}
                isMandatory
                multiline
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      </AccordionHeader>
      <MuiBox className="checkbox-container">
        <MuiTypography
          variant="h3Bold"
          className="select-preferences"
          component="h3"
        >
          {getMessageLabel(createSystemMessageSiteCoreKeys.selectPreferences)}
        </MuiTypography>

        <MuiGrid container>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormControllerCheckbox
              control={control}
              name={CreateMessageKey.addLinkInformation}
            >
              <MuiCheckbox
                checkboxLabel={getMessageLabel(
                  createSystemMessageSiteCoreKeys.addLinkToThisMessageCheckBox,
                )}
                id={`${pageId}-${CreateMessageKey.addLinkInformation}`}
                data-testid={`${pageId}-${CreateMessageKey.addLinkInformation}`}
              />
            </FormControllerCheckbox>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormControllerCheckbox
              control={control}
              name={CreateMessageKey.persistence}
            >
              <MuiCheckbox
                checkboxLabel={getMessageLabel(
                  createSystemMessageSiteCoreKeys.keepTheMessagePersistentCheckBox,
                )}
                id={`${pageId}-${CreateMessageKey.persistence}`}
                data-testid={`${pageId}-${CreateMessageKey.persistence}`}
              />
            </FormControllerCheckbox>
          </MuiGrid>
        </MuiGrid>

        {showLinkContainer && (
          <MuiGrid container>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateMessageKey.linkName}
              >
                <MuiTextField
                  id={`${pageId}-${CreateMessageKey.linkName}`}
                  label={getMessageLabel(
                    createSystemMessageSiteCoreKeys.linkName,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  shouldTrimSpaces
                  requiredARIALabel={commonARIALabel.required}
                  maxLength={MAX_LENGTH_75}
                  isMandatory
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController control={control} name={CreateMessageKey.linkUrl}>
                <MuiTextField
                  id={`${pageId}-${CreateMessageKey.linkUrl}`}
                  label={getMessageLabel(
                    createSystemMessageSiteCoreKeys.linkURL,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  shouldTrimSpaces
                  requiredARIALabel={commonARIALabel.required}
                  maxLength={MAX_LENGTH_250}
                  isMandatory
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateMessageKey.linkPosition}
              >
                <MuiSelect
                  label={getMessageLabel(
                    createSystemMessageSiteCoreKeys.linkPosition,
                  )}
                  options={fetchDropdownValues(
                    createSystemMessageSiteCoreKeys.createMessageKey,
                    createSystemMessageSiteCoreKeys.linkPositionList,
                  )}
                  value=""
                  id={`${pageId}-${CreateMessageKey.linkPosition}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
        )}
      </MuiBox>
      <AccordionHeader
        className="accordion-header"
        title={renderHeader(
          getMessageLabel(createSystemMessageSiteCoreKeys.durationSection),
        )}
        isExpanded={true}
        showExpandCollapseIcon={false}
      >
        <MuiGrid container className="add-form-container">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController control={control} name={CreateMessageKey.timeZone}>
              <MuiSelect
                label={getMessageLabel(
                  createSystemMessageSiteCoreKeys.timeZone,
                )}
                options={timeZoneListOptions}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${CreateMessageKey.timeZone}`}
                placeholder={commonLabels.selectPlaceHolder}
                isMandatory
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDateTimeFieldFormController
              format={userCountryDateFormatWithTime}
              control={control}
              name={CreateMessageKey.fromDate}
              label={getMessageLabel(
                createSystemMessageSiteCoreKeys.fromDateTime,
              )}
              id={`${pageId}-${CreateMessageKey.fromDate}`}
              selectDateTimeARIALabel={commonARIALabel.selectDate}
              value={getDateValue(
                CreateMessageKey.fromDate,
                selectedTimeZone || messageData?.timezone,
              )}
              disablePast
              {...(selectedTimeZone && {
                timezone:
                  (selectedTimeZone === "Europe/London" || 
                    selectedTimeZone === "Africa/Monrovia")
                    ? "Africa/Casablanca"
                    : selectedTimeZone,
              })}
              // added condition for GMT and Monrovia when the offset is 0, sending 1 (default for Africa/Monrovia) to fix issue temporarily
              disableManualInput
              requiredARIALabel={commonARIALabel.required}
              disableLabel={isDateDisabled}
              disabled={isDateDisabled}
              isMandatory
            />
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDateTimeFieldFormController
              format={userCountryDateFormatWithTime}
              control={control}
              name={CreateMessageKey.toDate}
              label={getMessageLabel(
                createSystemMessageSiteCoreKeys.toDateTime,
              )}
              id={`${pageId}-${CreateMessageKey.toDate}`}
              selectDateTimeARIALabel={commonARIALabel.selectDate}
              value={getDateValue(
                CreateMessageKey.toDate,
                selectedTimeZone || messageData?.timezone,
              )}
              disablePast
              {...(selectedTimeZone && {
                timezone:
                  (selectedTimeZone === "Europe/London" || 
                    selectedTimeZone === "Africa/Monrovia")
                    ? "Africa/Casablanca"
                    : selectedTimeZone,
              })}
              // added condition for GMT and Monrovia when the offset is 0, sending 1 (default for Africa/Monrovia) to fix issue temporarily
              disableManualInput
              requiredARIALabel={commonARIALabel.required}
              disableLabel={isDateDisabled}
              disabled={isDateDisabled}
              isMandatory
            />
          </MuiGrid>
        </MuiGrid>
      </AccordionHeader>
    </CreateSystemMessageFormWrapper>
  );
};
